import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Check, X, Loader2 } from 'lucide-react';
import { RootState } from '../../../configuration';
import { userService } from '../../../services/user.service';

const CustomUrlSetting = () => {
  const profile = useSelector((state: RootState) => state.features.auth.profile);
  const current = (profile as any)?.username || '';
  // Chưa có username thì trang cá nhân vẫn vào được bằng số thứ tự (/user/{sequenceId}).
  const sequenceId = (profile as any)?.sequenceId || '';
  const { host } = window.location;

  const [value, setValue] = useState('');
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<any>();

  const onChange = (v: string) => {
    // Chuẩn hoá ngay khi gõ: chữ thường, chỉ a-z 0-9 _, tối đa 30.
    const norm = v.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 30);
    setValue(norm);
  };

  useEffect(() => {
    setStatus(null);
    if (!value || value === current) return;
    if (value.length < 3) {
      setStatus({ ok: false, msg: 'Tối thiểu 3 ký tự.' });
      return;
    }
    setChecking(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await userService.checkUsernameAvailable(value);
        setStatus({ ok: !!res.available, msg: res.message || (res.available ? 'Còn trống.' : 'Đã có người dùng.') });
      } catch {
        setStatus(null);
      }
      setChecking(false);
    }, 450);
    return () => clearTimeout(debounceRef.current);
  }, [value, current]);

  const save = async () => {
    if (!status?.ok || saving) return;
    setSaving(true);
    try {
      const res = await userService.changeUsername(value);
      if (res.success) {
        toast.success(res.message || 'Đổi đường dẫn thành công!');
        setTimeout(() => window.location.assign(`/user/${res.username || value}`), 900);
      } else {
        toast.error(res.message || 'Đổi đường dẫn thất bại.');
      }
    } catch {
      toast.error('Đổi đường dẫn thất bại.');
    }
    setSaving(false);
  };

  return (
    <div className="p-4">
      {current ? (
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-300">
          Đặt đường dẫn riêng cho trang cá nhân của bạn. Chỉ đổi được <b>30 ngày / lần</b>.
        </p>
      ) : (
        <p className="mb-4 rounded-lg bg-blue-50 p-3 text-sm text-[#0057FF] dark:bg-blue-900/20 dark:text-blue-300">
          Bạn chưa có đường dẫn riêng. Đặt một tên ngắn để mọi người dễ tìm trang của bạn. Sau khi đặt, 30 ngày
          mới đổi lại được.
        </p>
      )}

      <div className="mb-1 text-sm dark:text-slate-200">Đường dẫn hiện tại:</div>
      <div className="mb-4 font-medium text-[#0057FF] break-all">
        {host}/user/{current || sequenceId || '—'}
      </div>

      <label className="mb-1 block text-sm font-medium dark:text-slate-200">Đường dẫn mới</label>
      <div className="flex items-stretch rounded-lg border border-gray-300 dark:border-slate-600 overflow-hidden max-w-[520px]">
        <span className="flex items-center px-3 bg-gray-50 dark:bg-slate-800 text-sm text-slate-500 whitespace-nowrap">
          {host}/user/
        </span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="ten-cua-ban"
          className="flex-1 min-w-0 px-3 py-2 text-sm outline-none dark:bg-slate-900 dark:text-white"
        />
        <span className="flex items-center px-3">
          {checking ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : status ? (
            status.ok ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-500" />
          ) : null}
        </span>
      </div>

      {status && (
        <div className={`mt-1 text-sm ${status.ok ? 'text-green-600' : 'text-red-500'}`}>{status.msg}</div>
      )}
      <div className="mt-1 text-xs text-slate-400">Dùng a–z, 0–9, dấu gạch dưới ( _ ). Dài 3–30 ký tự.</div>

      <button
        disabled={!status?.ok || saving}
        onClick={save}
        className="mt-4 h-10 min-w-[120px] rounded-md bg-[#0057FF] px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? 'Đang lưu…' : current ? 'Lưu đường dẫn' : 'Đặt đường dẫn'}
      </button>
    </div>
  );
};

export default CustomUrlSetting;
